import React, { useEffect } from "react";
import { Helmet } from "react-helmet-async";

const SITE = "https://collavue.com";
// 1200x630 social card. The 1024x1024 original crops badly in X and
// LinkedIn previews, and it is 1.4MB.
const DEFAULT_OG = `${SITE}/collavue-og.png`;

/**
 * Single source of truth for per-page head tags.
 *
 * `schema` accepts either one JSON-LD object or an array of them — rich pages
 * (guides, comparisons) need several graphs at once (Article + HowTo + FAQPage
 * + BreadcrumbList), and Google reads them fine as sibling script tags.
 */
export default function SEO({
  title,
  description,
  path = "",
  image = DEFAULT_OG,
  imageAlt,
  keywords,
  type = "website",
  schema,
  noindex = false,
  publishedTime,
  modifiedTime,
  author = "Collavue",
  section,
}) {
  const normalizedPath = path?.startsWith("/") ? path : `/${path || ""}`;
  // Canonicals must be exact: "/" keeps its slash, everything else drops any
  // trailing slash so /pricing and /pricing/ never compete with each other.
  const cleanPath =
    normalizedPath === "/" ? "/" : normalizedPath.replace(/\/+$/, "");
  const url = `${SITE}${cleanPath}`;
  const fullTitle = title?.includes("Collavue") ? title : `${title} | Collavue`;
  const robotsContent = noindex
    ? "noindex,nofollow,max-snippet:-1,max-image-preview:none"
    : "index,follow,max-snippet:-1,max-image-preview:large,max-video-preview:-1";

  const schemas = (Array.isArray(schema) ? schema : [schema]).filter(Boolean);

  // index.html ships a default description, canonical and social card so that
  // crawlers and unfurlers which never run JavaScript still see something
  // sensible. React 19 hoists the tags below alongside them rather than
  // replacing them, so the defaults have to be cleared once a route declares
  // its own — otherwise every page serves two canonicals pointing at "/".
  useEffect(() => {
    document
      .querySelectorAll("[data-default-seo]")
      .forEach((el) => el.remove());
  }, []);

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <meta name="robots" content={robotsContent} />
      <meta name="googlebot" content={robotsContent} />
      <meta name="author" content={author} />
      <link rel="canonical" href={url} />

      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={imageAlt || fullTitle} />
      <meta property="og:locale" content="en_US" />
      <meta property="og:site_name" content="Collavue" />

      {type === "article" && publishedTime && (
        <meta property="article:published_time" content={publishedTime} />
      )}
      {type === "article" && (modifiedTime || publishedTime) && (
        <meta
          property="article:modified_time"
          content={modifiedTime || publishedTime}
        />
      )}
      {type === "article" && <meta property="article:author" content={author} />}
      {type === "article" && section && (
        <meta property="article:section" content={section} />
      )}

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
      <meta name="twitter:image:alt" content={imageAlt || fullTitle} />
      <meta name="twitter:site" content="@collavue" />
      <meta name="twitter:creator" content="@collavue" />

      {schemas.map((s, i) => (
        <script type="application/ld+json" key={i}>
          {JSON.stringify(s)}
        </script>
      ))}
    </Helmet>
  );
}

/** Shared org node so every page's JSON-LD points at the same entity. */
export const ORGANIZATION_SCHEMA = {
  "@type": "Organization",
  "@id": `${SITE}/#organization`,
  name: "Collavue",
  url: SITE,
  logo: {
    "@type": "ImageObject",
    url: `${SITE}/collavue-logo-256.png`,
  },
  sameAs: ["https://x.com/collavue", "https://www.linkedin.com/company/collavue"],
};

/** BreadcrumbList builder — pass [{ name, path }] from the page. */
export function breadcrumbSchema(trail) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: trail.map((item, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: item.name,
      item: `${SITE}${item.path}`,
    })),
  };
}

/** FAQPage builder — pass [{ q, a }] from the page. */
export function faqSchema(faqs) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
}

export { SITE as SITE_URL, DEFAULT_OG as DEFAULT_OG_IMAGE };
